import React, { useMemo, useState, createContext, useContext } from "react";
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine
} from "recharts";
import {
  Stethoscope, Activity, Dumbbell, Apple, Trophy, Radar,
  Printer, Download, MapPin, Users, TrendingUp, Banknote, Target, CheckCircle2, Calendar, Building2, ArrowDownRight, ArrowUpRight, Quote, CalendarCheck, Check, Minus, CircleDot, Mail, Phone, Brain, Copy, Globe
} from "lucide-react";
import Logo from "@/components/Logo";
import * as DATA_EN from "@/data/planData";
import * as DATA_ES from "@/data/planData.es";

// Context provides current-language data + language switcher
const DataContext = createContext({ ...DATA_EN, lang: "en", setLang: () => {} });
const useData = () => useContext(DataContext);

// Number formatting locale-aware
const fmtUSD = (n) => "$" + n.toLocaleString("en-US");
const fmtUSDk = (n) => {
  if (Math.abs(n) >= 1_000_000) return "$" + (n / 1_000_000).toFixed(2).replace(/\.00$/, "") + "M";
  if (Math.abs(n) >= 1_000) return "$" + (n / 1_000).toFixed(0) + "K";
  return fmtUSD(n);
};

const iconMap = { stethoscope: Stethoscope, activity: Activity, dumbbell: Dumbbell, apple: Apple, trophy: Trophy, radar: Radar, brain: Brain };

// --- Reusable section header ---
const SectionTitle = ({ eyebrow, title, kicker, dark = false }) => {
  const eyebrowColor = dark ? "#c9a24b" : "var(--wgt-gold-dark)";
  const titleColor = dark ? "#f4ecd8" : "var(--wgt-green-900)";
  const kickerColor = dark ? "#e9ddbb" : "var(--wgt-ink-soft)";
  return (
    <div className="mb-10 print-avoid-break">
      {eyebrow && (
        <div className="font-sans-clean text-xs tracking-[0.3em] uppercase mb-3" style={{ color: eyebrowColor }} data-testid="section-eyebrow">
          {eyebrow}
        </div>
      )}
      <h2 className="font-serif-display text-4xl md:text-5xl leading-tight" style={{ color: titleColor }}>
        {title}
      </h2>
      <div className="divider-gold mt-6 w-full max-w-xs" />
      {kicker && <p className="mt-5 font-serif text-lg md:text-xl max-w-3xl" style={{ color: kickerColor }}>{kicker}</p>}
    </div>
  );
};

const Nav = () => {
  const { UI, lang, setLang } = useData();
  return (
  <nav data-testid="top-nav" className="no-print sticky top-0 z-50 backdrop-blur-md" style={{ background: "rgba(11,61,46,0.95)", color: "var(--wgt-cream-100)" }}>
    <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <Logo size={40} variant="light" />
        <div className="leading-tight">
          <div className="font-serif-display text-lg">WILLIAMS Golf Therapy</div>
          <div className="font-sans-clean text-[10px] tracking-[0.25em] uppercase opacity-80">{UI.nav.investorBusinessPlan}</div>
        </div>
      </div>
      <div className="hidden md:flex items-center gap-5 text-sm font-sans-clean">
        {[
          [UI.nav.items.vision, "vision"], [UI.nav.items.market, "market"], [UI.nav.items.services, "services"],
          [UI.nav.items.financials, "financials"], [UI.nav.items.investment, "investment"], [UI.nav.items.roadmap, "roadmap"], [UI.nav.items.contact, "contact"]
        ].map(([label, id]) => (
          <a key={id} href={`#${id}`} data-testid={`nav-${id}`} className="hover:text-[--wgt-gold] transition-colors" style={{ "--wgt-gold": "#c9a24b" }}>
            {label}
          </a>
        ))}
      </div>
      <div className="flex items-center gap-2">
        {/* Language toggle */}
        <div data-testid="lang-toggle" className="flex items-center rounded-full overflow-hidden text-xs font-sans-clean font-semibold" style={{ border: "1px solid rgba(201,162,75,0.55)" }}>
          <button
            data-testid="lang-en"
            onClick={() => setLang("en")}
            className="px-2.5 py-1.5 transition-colors"
            style={{ background: lang === "en" ? "#c9a24b" : "transparent", color: lang === "en" ? "#0b3d2e" : "#f4ecd8" }}
            aria-pressed={lang === "en"}
          >EN</button>
          <button
            data-testid="lang-es"
            onClick={() => setLang("es")}
            className="px-2.5 py-1.5 transition-colors"
            style={{ background: lang === "es" ? "#c9a24b" : "transparent", color: lang === "es" ? "#0b3d2e" : "#f4ecd8" }}
            aria-pressed={lang === "es"}
          >ES</button>
        </div>
        <button
          data-testid="download-pdf-btn"
          onClick={() => window.print()}
          className="flex items-center gap-2 px-4 py-2 rounded-full font-sans-clean text-sm font-semibold transition-all hover:scale-[1.03]"
          style={{ background: "#c9a24b", color: "#0b3d2e" }}
        >
          <Download size={16} /> {UI.nav.downloadPdf}
        </button>
      </div>
    </div>
  </nav>
  );
};

// --- Hero ---
const Hero = () => {
  const { BRAND, LOCATION, HERO_KPIS, HERO_BG, UI } = useData();
  return (
  <section id="vision" data-testid="hero-section" className="relative paper-deep grain overflow-hidden">
    {/* Subtle background image */}
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage: `url(${HERO_BG})`,
        backgroundSize: "cover",
        backgroundPosition: "center right",
        opacity: 0.18,
        filter: "blur(1px)",
      }}
    />
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background: "linear-gradient(135deg, rgba(6,42,32,0.92) 0%, rgba(11,61,46,0.78) 55%, rgba(6,42,32,0.95) 100%)",
      }}
    />
    <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
      style={{
        backgroundImage: "radial-gradient(circle at 15% 20%, #c9a24b 0%, transparent 35%), radial-gradient(circle at 85% 80%, #f4ecd8 0%, transparent 40%)"
      }} />
    <div className="relative max-w-6xl mx-auto px-6 py-20 md:py-28">
      <div className="flex flex-col md:flex-row items-start md:items-center gap-10">
        <div className="shrink-0 fade-up">
          <Logo size={150} variant="light" />
        </div>
        <div className="flex-1 fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="font-sans-clean tracking-[0.35em] text-xs uppercase mb-4" style={{ color: "#c9a24b" }}>
            {UI.hero.eyebrow}
          </div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.02]" style={{ color: "var(--wgt-cream-100)" }}>
            {BRAND.name}
          </h1>
          <div className="mt-5 font-serif text-2xl md:text-3xl italic" style={{ color: "#e9ddbb" }}>
            {BRAND.tagline}
          </div>
          <div className="mt-3 font-sans-clean text-sm tracking-[0.4em] uppercase" style={{ color: "#c9a24b" }}>
            {BRAND.pillars}
          </div>
        </div>
      </div>

      <p className="mt-12 max-w-4xl font-serif text-xl md:text-2xl leading-relaxed fade-up" style={{ color: "#f4ecd8", animationDelay: "0.3s" }}>
        <Quote className="inline-block mr-2 -mt-2 opacity-60" size={22} />
        {BRAND.vision}
      </p>

      <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-5 fade-up" style={{ animationDelay: "0.45s" }}>
        {HERO_KPIS.map((k, i) => (
          <div key={i} data-testid={`hero-kpi-${i}`} className="p-5 rounded-sm border" style={{ borderColor: "rgba(201,162,75,0.45)", background: "rgba(6,42,32,0.45)" }}>
            <div className="font-serif-display text-3xl md:text-4xl" style={{ color: "#c9a24b" }}>{k.value}</div>
            <div className="font-sans-clean text-[11px] tracking-[0.2em] uppercase mt-2" style={{ color: "#f4ecd8" }}>{k.label}</div>
            <div className="font-sans-clean text-xs mt-1" style={{ color: "#e9ddbb", opacity: 0.85 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      <div className="mt-14 flex flex-wrap gap-6 items-center font-sans-clean text-sm" style={{ color: "#e9ddbb" }}>
        <div className="flex items-center gap-2"><MapPin size={16} style={{ color: "#c9a24b" }} /> {LOCATION.address}</div>
        <div className="flex items-center gap-2"><Building2 size={16} style={{ color: "#c9a24b" }} /> {LOCATION.proximity}</div>
        <div className="flex items-center gap-2"><Users size={16} style={{ color: "#c9a24b" }} /> {LOCATION.trafficCount} {LOCATION.trafficNote}</div>
      </div>
    </div>
  </section>
  );
};

// --- Founder ---
const Founder = () => {
  const { FOUNDER, FOUNDER_IMAGES, UI } = useData();
  return (
  <section data-testid="founder-section" className="max-w-6xl mx-auto px-6 py-20 print-break-before">
    <SectionTitle eyebrow={UI.founder.title} title={FOUNDER.name} kicker={`${FOUNDER.experience} — ${UI.founder.kicker}`} />
    <div className="grid md:grid-cols-12 gap-8 items-start">
      {/* Photo column */}
      <div className="md:col-span-5 space-y-4 print-avoid-break">
        <div className="relative paper rounded-sm overflow-hidden" style={{ aspectRatio: "4 / 5" }}>
          <img
            src={FOUNDER_IMAGES.primary}
            alt={`${FOUNDER.name}`}
            className="w-full h-full object-cover"
            data-testid="founder-photo-primary"
          />
          <div className="absolute bottom-0 left-0 right-0 p-4" style={{ background: "linear-gradient(to top, rgba(11,61,46,0.85), transparent)" }}>
            <div className="font-sans-clean text-[10px] tracking-[0.3em] uppercase" style={{ color: "#c9a24b" }}>{UI.founder.photoLabel}</div>
            <div className="font-serif-display text-xl" style={{ color: "#f4ecd8" }}>{FOUNDER.name}</div>
          </div>
        </div>
        <div className="paper rounded-sm overflow-hidden" style={{ aspectRatio: "16 / 10" }}>
          <img
            src={FOUNDER_IMAGES.secondary}
            alt={FOUNDER.name}
            className="w-full h-full object-cover"
            data-testid="founder-photo-secondary"
          />
        </div>
      </div>

      {/* Credentials + Why card */}
      <div className="md:col-span-7 space-y-5">
        <ul className="space-y-3 font-sans-clean">
          {FOUNDER.credentials.map((c, i) => (
            <li key={i} className="flex items-start gap-3 paper p-4 rounded-sm">
              <CheckCircle2 size={20} style={{ color: "var(--wgt-green-700)" }} className="shrink-0 mt-0.5" />
              <span className="text-base" style={{ color: "var(--wgt-ink)" }}>{c}</span>
            </li>
          ))}
        </ul>
        <div className="paper p-7 rounded-sm relative grain">
          <div className="font-serif-display text-2xl mb-3" style={{ color: "var(--wgt-green-900)" }}>{UI.founder.whyTitle}</div>
          <p className="font-serif text-base md:text-lg leading-relaxed" style={{ color: "var(--wgt-ink-soft)" }}>
            {UI.founder.whyText}
          </p>
        </div>
      </div>
    </div>
  </section>
  );
};

// --- Market ---
const Market = () => {
  const { MARKET, LOCATION, UI } = useData();
  const { golfers, courses } = MARKET;
  return (
    <section id="market" data-testid="market-section" className="paper-deep grain print-break-before">
      <div className="max-w-6xl mx-auto px-6 py-20 relative">
        <SectionTitle eyebrow={UI.market.eyebrow} title={UI.market.title} dark kicker={UI.market.kicker} />
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { label: UI.market.miamiDadeLabel, value: golfers.miamiDade.toLocaleString(), sub: UI.market.coursesNote(courses.miamiDade) },
            { label: UI.market.browardLabel, value: golfers.broward.toLocaleString(), sub: UI.market.coursesNote(courses.broward) },
            { label: UI.market.palmBeachLabel, value: golfers.palmBeach.toLocaleString(), sub: UI.market.coursesNote(courses.palmBeach) },
          ].map((c, i) => (
            <div key={i} data-testid={`market-card-${i}`} className="p-7 rounded-sm" style={{ background: "rgba(244,236,216,0.06)", border: "1px solid rgba(201,162,75,0.35)" }}>
              <div className="font-sans-clean text-[11px] tracking-[0.3em] uppercase mb-3" style={{ color: "#c9a24b" }}>{c.label}</div>
              <div className="font-serif-display text-5xl" style={{ color: "#f4ecd8" }}>{c.value}</div>
              <div className="font-sans-clean text-sm mt-3" style={{ color: "#e9ddbb" }}>{c.sub}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          <div className="p-7 rounded-sm" style={{ background: "rgba(201,162,75,0.12)", border: "1px solid #c9a24b" }}>
            <div className="font-sans-clean text-[11px] tracking-[0.3em] uppercase mb-2" style={{ color: "#c9a24b" }}>{UI.market.totalLabel}</div>
            <div className="font-serif-display text-6xl" style={{ color: "#f4ecd8" }}>{golfers.total.toLocaleString()}</div>
            <div className="font-sans-clean text-sm mt-3" style={{ color: "#f4ecd8" }}>{UI.market.totalNote(courses.total)}</div>
          </div>
          <div className="p-7 rounded-sm" style={{ background: "rgba(244,236,216,0.06)", border: "1px solid rgba(201,162,75,0.35)" }}>
            <div className="font-sans-clean text-[11px] tracking-[0.3em] uppercase mb-2" style={{ color: "#c9a24b" }}>{UI.market.shareLabel}</div>
            <div className="flex items-end gap-6 mt-2">
              <div>
                <div className="font-serif-display text-5xl" style={{ color: "#f4ecd8" }}>{MARKET.perLocationCapture}%</div>
                <div className="font-sans-clean text-xs mt-1" style={{ color: "#e9ddbb" }}>{UI.market.perSite}</div>
              </div>
              <div className="opacity-60 text-2xl" style={{ color: "#c9a24b" }}>→</div>
              <div>
                <div className="font-serif-display text-5xl" style={{ color: "#f4ecd8" }}>{MARKET.fullFranchiseCapture}%</div>
                <div className="font-sans-clean text-xs mt-1" style={{ color: "#e9ddbb" }}>{UI.market.fullRollout}</div>
              </div>
            </div>
            <div className="font-sans-clean text-sm mt-5" style={{ color: "#e9ddbb" }}>
              {UI.market.shareCaption}
            </div>
          </div>
        </div>

        <div className="mt-12 paper p-7 rounded-sm print-avoid-break">
          <div className="font-serif-display text-2xl mb-3" style={{ color: "var(--wgt-green-900)" }}>{UI.market.locationTitle}: {LOCATION.address}</div>
          <div className="grid md:grid-cols-4 gap-6 font-sans-clean text-sm" style={{ color: "var(--wgt-ink)" }}>
            <div><div className="text-xs uppercase tracking-wider opacity-60 mb-1">{UI.market.squareFeetLabel}</div><div className="font-semibold text-lg">{LOCATION.squareFeet}</div></div>
            <div><div className="text-xs uppercase tracking-wider opacity-60 mb-1">{UI.market.countryClubLabel}</div><div className="font-semibold text-lg">{LOCATION.proximity}</div></div>
            <div><div className="text-xs uppercase tracking-wider opacity-60 mb-1">{UI.market.trafficLabel}</div><div className="font-semibold text-lg">{LOCATION.trafficCount}+ {UI.market.vehiclesUnit}</div></div>
            <div><div className="text-xs uppercase tracking-wider opacity-60 mb-1">{UI.market.visibilityLabel}</div><div className="font-semibold text-lg">{UI.market.dayNight}</div></div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Differentiators ---
const Differentiators = () => {
  const { DIFFERENTIATORS, UI } = useData();
  return (
  <section data-testid="diff-section" className="max-w-6xl mx-auto px-6 py-20 print-break-before">
    <SectionTitle eyebrow={UI.diff.eyebrow} title={UI.diff.title} />
    <div className="grid md:grid-cols-2 gap-6">
      {DIFFERENTIATORS.map((d, i) => (
        <div key={i} data-testid={`diff-card-${i}`} className="paper p-7 rounded-sm relative print-avoid-break">
          <div className="absolute top-5 right-5 font-serif-display text-5xl opacity-15" style={{ color: "var(--wgt-green-800)" }}>0{i + 1}</div>
          <div className="font-serif-display text-2xl mb-3" style={{ color: "var(--wgt-green-900)" }}>{d.title}</div>
          <p className="font-serif text-lg leading-relaxed" style={{ color: "var(--wgt-ink-soft)" }}>{d.text}</p>
        </div>
      ))}
    </div>
  </section>
  );
};

// --- Services ---
const Services = () => {
  const { SERVICES, UI } = useData();
  return (
  <section id="services" data-testid="services-section" className="paper-deep grain print-break-before">
    <div className="max-w-6xl mx-auto px-6 py-20 relative">
      <SectionTitle eyebrow={UI.services.eyebrow} title={UI.services.title} dark kicker={UI.services.kicker} />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {SERVICES.map((s, i) => {
          const Icon = iconMap[s.icon] || Activity;
          // Center the last orphan card when grid leaves a single item on the bottom row
          const total = SERVICES.length;
          const isLgOrphan = i === total - 1 && total % 3 === 1;
          const isMdOrphan = i === total - 1 && total % 2 === 1;
          const orphanCls = `${isMdOrphan ? "md:col-start-1 md:col-end-3" : ""} ${isLgOrphan ? "lg:col-start-2 lg:col-end-3" : ""}`;
          return (
            <div key={i} data-testid={`service-card-${i}`} className={`rounded-sm overflow-hidden print-avoid-break flex flex-col ${orphanCls}`} style={{ background: "rgba(244,236,216,0.06)", border: "1px solid rgba(201,162,75,0.35)" }}>
              {s.image && (
                <div className="w-full h-40 overflow-hidden" style={{ borderBottom: "1px solid rgba(201,162,75,0.3)" }}>
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover" style={{ filter: "saturate(0.9)" }} />
                </div>
              )}
              <div className="p-6 flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded-full" style={{ background: "#c9a24b" }}>
                    <Icon size={20} style={{ color: "#0b3d2e" }} />
                  </div>
                  <div className="font-serif-display text-2xl" style={{ color: "#f4ecd8" }}>{s.title}</div>
                </div>
                <ul className="space-y-2 font-sans-clean text-sm" style={{ color: "#e9ddbb" }}>
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2"><span style={{ color: "#c9a24b" }}>◆</span> {b}</li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
  );
};

// --- Facility Gallery ---
const FacilityGallery = () => {
  const { FACILITY_GALLERY, UI } = useData();
  return (
  <section data-testid="facility-section" className="max-w-6xl mx-auto px-6 py-20 print-break-before">
    <SectionTitle eyebrow={UI.facility.eyebrow} title={UI.facility.title} kicker={UI.facility.kicker} />
    <div className="grid md:grid-cols-2 gap-6">
      {FACILITY_GALLERY.map((g, i) => (
        <figure key={i} data-testid={`facility-card-${i}`} className="paper rounded-sm overflow-hidden print-avoid-break group" style={{ border: "1px solid var(--wgt-cream-200)" }}>
          <div className="w-full overflow-hidden" style={{ aspectRatio: "16 / 10" }}>
            <img
              src={g.image}
              alt={g.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
          </div>
          <figcaption className="p-5">
            <div className="font-sans-clean text-[11px] tracking-[0.3em] uppercase mb-1" style={{ color: "var(--wgt-gold-dark)" }}>{g.eyebrow}</div>
            <div className="font-serif-display text-xl mb-2" style={{ color: "var(--wgt-green-900)" }}>{g.title}</div>
            <p className="font-serif text-sm leading-relaxed" style={{ color: "var(--wgt-ink-soft)" }}>{g.text}</p>
          </figcaption>
        </figure>
      ))}
    </div>
  </section>
  );
};

// --- Financials ---
const Financials = () => {
  const { MONTHLY_REVENUE, MONTHLY_OPEX, STARTUP_CAPITAL, PROJECTIONS, YEAR3_NET_AFTER_DEBT_TAX, UI } = useData();
  const startupTotal = useMemo(() => STARTUP_CAPITAL.items.reduce((a, b) => a + b.amount, 0), [STARTUP_CAPITAL]);
  return (
    <section id="financials" data-testid="financials-section" className="max-w-6xl mx-auto px-6 py-20 print-break-before">
      <SectionTitle eyebrow={UI.financials.eyebrow} title={UI.financials.title} kicker={UI.financials.kicker} />

      {/* Monthly economics */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        <div className="paper p-7 rounded-sm">
          <div className="font-serif-display text-2xl mb-5" style={{ color: "var(--wgt-green-900)" }}>{UI.financials.monthlyRevenue}</div>
          <Row label={UI.financials.revenue.membership} value={fmtUSD(MONTHLY_REVENUE.membership)} bold />
          <Row label={UI.financials.revenue.performance} value={fmtUSD(MONTHLY_REVENUE.performancePackages)} />
          <Row label={UI.financials.revenue.insurance} value={fmtUSD(MONTHLY_REVENUE.insuranceReimbursement)} />
          <Row label={UI.financials.revenue.ancillary} value={fmtUSD(MONTHLY_REVENUE.ancillary)} />
          <div className="rule-double my-4" />
          <Row label={UI.financials.revenue.total} value={fmtUSD(MONTHLY_REVENUE.total)} highlight />
        </div>

        <div className="paper p-7 rounded-sm">
          <div className="font-serif-display text-2xl mb-5" style={{ color: "var(--wgt-green-900)" }}>{UI.financials.monthlyOpex}</div>
          <Row label={UI.financials.opex.rent} value={fmtUSD(MONTHLY_OPEX.baseRentNNN)} />
          <Row label={UI.financials.opex.payroll} value={fmtUSD(MONTHLY_OPEX.payroll)} />
          <Row label={UI.financials.opex.marketing} value={fmtUSD(MONTHLY_OPEX.marketing)} />
          <Row label={UI.financials.opex.utilities} value={fmtUSD(MONTHLY_OPEX.utilitiesInternet)} />
          <Row label={UI.financials.opex.supplies} value={fmtUSD(MONTHLY_OPEX.suppliesLaundry)} />
          <Row label={UI.financials.opex.professional} value={fmtUSD(MONTHLY_OPEX.professionalServices)} />
          <Row label={UI.financials.opex.insurance} value={fmtUSD(MONTHLY_OPEX.insurance)} />
          <Row label={UI.financials.opex.loan} value={fmtUSD(MONTHLY_OPEX.loanPayment)} />
          <div className="rule-double my-4" />
          <Row label={UI.financials.opex.total} value={fmtUSD(MONTHLY_OPEX.total)} highlight />
        </div>
      </div>

      {/* Margin band */}
      <div className="paper-deep rounded-sm p-8 mb-12 print-avoid-break grain relative">
        <div className="grid md:grid-cols-3 gap-6 relative">
          <Metric label={UI.financials.netProfitMonthly} value={fmtUSD(MONTHLY_REVENUE.total - MONTHLY_OPEX.total)} />
          <Metric label={UI.financials.operatingMargin} value={`${Math.round(((MONTHLY_REVENUE.total - MONTHLY_OPEX.total) / MONTHLY_REVENUE.total) * 100)}%`} />
          <Metric label={UI.financials.year3Net} value={fmtUSD(YEAR3_NET_AFTER_DEBT_TAX)} />
        </div>
      </div>

      {/* Startup capital */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        <div className="paper p-7 rounded-sm">
          <div className="font-serif-display text-2xl mb-5" style={{ color: "var(--wgt-green-900)" }}>{UI.financials.startupAlloc}</div>
          {STARTUP_CAPITAL.items.map((item, i) => (
            <Row key={i} label={item.label} value={fmtUSD(item.amount)} />
          ))}
          <div className="rule-double my-4" />
          <Row label={UI.financials.totalCapital} value={fmtUSD(STARTUP_CAPITAL.total)} highlight />
        </div>

        <div className="paper p-7 rounded-sm relative">
          <div className="font-serif-display text-2xl mb-5" style={{ color: "var(--wgt-green-900)" }}>{UI.financials.useOfFunds}</div>
          <UseOfFundsBar items={STARTUP_CAPITAL.items} total={startupTotal} />
        </div>
      </div>

      {/* 5-year projection chart */}
      <ProjectionChart />

      {/* 5-year projection table */}
      <div className="paper rounded-sm overflow-hidden print-avoid-break">
        <div className="font-serif-display text-2xl p-6 pb-3" style={{ color: "var(--wgt-green-900)" }}>{UI.financials.projectionTitle}</div>
        <div className="overflow-x-auto">
          <table className="w-full font-sans-clean text-sm">
            <thead>
              <tr style={{ background: "var(--wgt-green-800)", color: "var(--wgt-cream-100)" }}>
                <th className="text-left p-4">{UI.financials.tableHead.year}</th>
                <th className="text-left p-4">{UI.financials.tableHead.members}</th>
                <th className="text-right p-4">{UI.financials.tableHead.revenue}</th>
                <th className="text-right p-4">{UI.financials.tableHead.opex}</th>
                <th className="text-right p-4">{UI.financials.tableHead.net}</th>
                <th className="text-left p-4">{UI.financials.tableHead.milestone}</th>
              </tr>
            </thead>
            <tbody>
              {PROJECTIONS.map((p, i) => (
                <tr key={i} data-testid={`proj-row-${i}`} className="border-b" style={{ borderColor: "var(--wgt-cream-200)" }}>
                  <td className="p-4 font-semibold" style={{ color: "var(--wgt-green-900)" }}>{p.year}</td>
                  <td className="p-4" style={{ color: "var(--wgt-ink-soft)" }}>{p.members} {UI.financials.membersUnit} · {p.occupancy}</td>
                  <td className="p-4 text-right font-serif-display text-lg" style={{ color: "var(--wgt-green-800)" }}>{fmtUSDk(p.revenue)}</td>
                  <td className="p-4 text-right" style={{ color: "var(--wgt-ink-soft)" }}>{fmtUSDk(p.opex)}</td>
                  <td className="p-4 text-right font-serif-display text-lg" style={{ color: p.netProfit < 0 ? "#a14b3a" : "var(--wgt-green-700)" }}>
                    {p.netProfit < 0 ? "(" + fmtUSDk(Math.abs(p.netProfit)) + ")" : fmtUSDk(p.netProfit)}
                  </td>
                  <td className="p-4 text-xs" style={{ color: "var(--wgt-ink-soft)" }}>{p.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 text-xs font-sans-clean" style={{ color: "var(--wgt-ink-soft)" }}>
          {UI.financials.tableFootnote}
        </div>
      </div>
    </section>
  );
};

const Row = ({ label, value, bold = false, highlight = false }) => (
  <div className={`flex items-center justify-between py-2.5 ${bold ? "font-semibold" : ""}`} style={{ color: highlight ? "var(--wgt-green-900)" : "var(--wgt-ink)" }}>
    <span className="font-sans-clean text-sm">{label}</span>
    <span className={highlight ? "font-serif-display text-2xl" : "font-sans-clean text-sm font-medium"}>{value}</span>
  </div>
);

const Metric = ({ label, value }) => (
  <div>
    <div className="font-sans-clean text-[11px] tracking-[0.3em] uppercase mb-2" style={{ color: "#c9a24b" }}>{label}</div>
    <div className="font-serif-display text-4xl md:text-5xl" style={{ color: "#f4ecd8" }}>{value}</div>
  </div>
);

const UseOfFundsBar = ({ items, total }) => {
  const colors = ["#0b3d2e", "#155a3f", "#2f7a55", "#5a9a72", "#c9a24b", "#8f6f2b", "#3b6b4f", "#7a8d3a", "#a14b3a"];
  return (
    <div>
      <div className="flex w-full h-7 rounded-sm overflow-hidden mb-5" style={{ border: "1px solid var(--wgt-cream-200)" }}>
        {items.map((item, i) => (
          <div key={i} style={{ width: `${(item.amount / total) * 100}%`, background: colors[i % colors.length] }} title={`${item.label}: ${fmtUSD(item.amount)}`} />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-1.5 font-sans-clean text-xs" style={{ color: "var(--wgt-ink-soft)" }}>
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: colors[i % colors.length] }} />
            <span className="flex-1">{item.label}</span>
            <span className="font-semibold" style={{ color: "var(--wgt-green-900)" }}>{((item.amount / total) * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- Investment ---
const Investment = () => {
  const { EQUITY, INVESTMENT_OPTIONS, EXIT_SCENARIOS, UI } = useData();
  return (
  <section id="investment" data-testid="investment-section" className="paper-deep grain print-break-before">
    <div className="max-w-6xl mx-auto px-6 py-20 relative">
      <SectionTitle eyebrow={UI.investment.eyebrow} title={UI.investment.title} dark kicker={UI.investment.kicker} />

      {/* Equity table */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        <div className="p-7 rounded-sm" style={{ background: "rgba(244,236,216,0.06)", border: "1px solid rgba(201,162,75,0.45)" }}>
          <div className="font-serif-display text-2xl mb-5" style={{ color: "#f4ecd8" }}>{UI.investment.capTable}</div>
          {EQUITY.structure.map((s, i) => (
            <div key={i} className="flex items-center justify-between py-3 border-b" style={{ borderColor: "rgba(201,162,75,0.25)" }}>
              <span className="font-sans-clean text-sm" style={{ color: "#f4ecd8" }}>{s.holder}</span>
              <span className="font-serif-display text-3xl" style={{ color: "#c9a24b" }}>{s.percent}%</span>
            </div>
          ))}
          <div className="flex items-center justify-between py-3 mt-2" style={{ color: "#c9a24b" }}>
            <span className="font-sans-clean text-sm uppercase tracking-[0.2em]">{UI.investment.totalEquity}</span>
            <span className="font-serif-display text-3xl">{EQUITY.totalCheck}%</span>
          </div>
        </div>

        <div className="p-7 rounded-sm" style={{ background: "rgba(201,162,75,0.12)", border: "1px solid #c9a24b" }}>
          <div className="font-sans-clean text-[11px] tracking-[0.3em] uppercase mb-3" style={{ color: "#c9a24b" }}>{EQUITY.finderFee.label}</div>
          <div className="font-serif-display text-4xl mb-3" style={{ color: "#f4ecd8" }}>{UI.investment.finderFeeAmount}</div>
          <p className="font-serif text-lg leading-relaxed" style={{ color: "#e9ddbb" }}>
            {EQUITY.finderFee.description}
          </p>
          <div className="mt-5 p-4 rounded-sm" style={{ background: "rgba(11,61,46,0.6)" }}>
            <div className="text-xs font-sans-clean tracking-wider uppercase mb-1" style={{ color: "#c9a24b" }}>{UI.investment.onSeed}</div>
            <div className="font-serif-display text-3xl" style={{ color: "#f4ecd8" }}>{fmtUSD(EQUITY.finderFee.amountOnSeed)} {UI.investment.feeAmount}</div>
          </div>
        </div>
      </div>

      {/* Three options */}
      <div className="grid md:grid-cols-3 gap-6 mb-12">
        {INVESTMENT_OPTIONS.map((opt, i) => (
          <div key={i} data-testid={`invest-option-${i}`} className="p-7 rounded-sm print-avoid-break" style={{ background: "rgba(244,236,216,0.06)", border: "1px solid rgba(201,162,75,0.35)" }}>
            <div className="font-serif-display text-2xl mb-1" style={{ color: "#f4ecd8" }}>{opt.title}</div>
            <div className="font-sans-clean text-xs tracking-[0.2em] uppercase mb-4" style={{ color: "#c9a24b" }}>{opt.terms}</div>
            <p className="font-serif text-base leading-relaxed mb-5" style={{ color: "#e9ddbb" }}>{opt.description}</p>
            <div className="text-xs font-sans-clean pt-4 border-t" style={{ color: "#c9a24b", borderColor: "rgba(201,162,75,0.3)" }}>
              {UI.investment.idealFor} <span style={{ color: "#f4ecd8" }}>{opt.ideal}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Exit scenarios */}
      <div className="paper rounded-sm overflow-hidden">
        <div className="px-6 py-4" style={{ background: "var(--wgt-green-800)", color: "var(--wgt-cream-100)" }}>
          <div className="font-serif-display text-2xl">{UI.investment.exitTitle}</div>
          <div className="font-sans-clean text-sm opacity-85 mt-1">{UI.investment.exitSub}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full font-sans-clean text-sm">
            <thead>
              <tr style={{ background: "var(--wgt-cream-100)", color: "var(--wgt-green-900)" }}>
                <th className="text-left p-4">{UI.investment.exitTable.salePrice}</th>
                <th className="text-right p-4">{UI.investment.exitTable.founder}</th>
                <th className="text-right p-4">{UI.investment.exitTable.investors}</th>
                <th className="text-right p-4">{UI.investment.exitTable.multiple}</th>
              </tr>
            </thead>
            <tbody>
              {EXIT_SCENARIOS.map((s, i) => (
                <tr key={i} data-testid={`exit-row-${i}`} className="border-b" style={{ borderColor: "var(--wgt-cream-200)" }}>
                  <td className="p-4 font-serif-display text-2xl" style={{ color: "var(--wgt-green-900)" }}>{fmtUSDk(s.sale)}</td>
                  <td className="p-4 text-right" style={{ color: "var(--wgt-ink)" }}>{fmtUSDk(s.founder)}</td>
                  <td className="p-4 text-right font-semibold" style={{ color: "var(--wgt-green-700)" }}>{fmtUSDk(s.investors)}</td>
                  <td className="p-4 text-right font-serif-display text-2xl" style={{ color: "var(--wgt-gold-dark)" }}>{s.multiple}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </section>
  );
};

// --- Roadmap ---
const Roadmap = () => {
  const { FRANCHISE_ROADMAP, UI } = useData();
  return (
  <section id="roadmap" data-testid="roadmap-section" className="max-w-6xl mx-auto px-6 py-20 print-break-before">
    <SectionTitle eyebrow={UI.roadmap.eyebrow} title={UI.roadmap.title} kicker={UI.roadmap.kicker} />
    <div className="relative">
      <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px" style={{ background: "var(--wgt-gold)" }} />
      {FRANCHISE_ROADMAP.map((step, i) => (
        <div key={i} data-testid={`roadmap-step-${i}`} className={`relative mb-10 md:mb-14 md:w-1/2 ${i % 2 === 0 ? "md:pr-12" : "md:ml-auto md:pl-12"} pl-12 md:pl-0 print-avoid-break`}>
          <div className={`absolute top-3 ${i % 2 === 0 ? "md:right-[-9px] left-[-9px] md:left-auto" : "left-[-9px]"} w-5 h-5 rounded-full border-2`}
            style={{ background: "var(--wgt-gold)", borderColor: "var(--wgt-green-800)" }} />
          <div className="paper p-6 rounded-sm">
            <div className="font-sans-clean text-xs tracking-[0.3em] uppercase mb-2" style={{ color: "var(--wgt-gold-dark)" }}>{step.phase}</div>
            <div className="font-serif-display text-2xl mb-2" style={{ color: "var(--wgt-green-900)" }}>{step.milestone}</div>
            <div className="font-serif text-base" style={{ color: "var(--wgt-ink-soft)" }}>{step.detail}</div>
          </div>
        </div>
      ))}
    </div>
  </section>
  );
};

// --- Why Now ---
const WhyNow = () => {
  const { WHY_NOW, UI } = useData();
  return (
  <section data-testid="why-now-section" className="paper-deep grain print-break-before">
    <div className="max-w-6xl mx-auto px-6 py-20 relative">
      <SectionTitle eyebrow={UI.whyNow.eyebrow} title={UI.whyNow.title} dark />
      <ul className="grid md:grid-cols-2 gap-5">
        {WHY_NOW.map((line, i) => (
          <li key={i} data-testid={`why-now-${i}`} className="flex items-start gap-4 p-5 rounded-sm" style={{ background: "rgba(244,236,216,0.05)", border: "1px solid rgba(201,162,75,0.3)" }}>
            <div className="font-serif-display text-4xl shrink-0" style={{ color: "#c9a24b" }}>{(i + 1).toString().padStart(2, "0")}</div>
            <div className="font-serif text-lg leading-relaxed" style={{ color: "#f4ecd8" }}>{line}</div>
          </li>
        ))}
      </ul>
    </div>
  </section>
  );
};

// --- Footer / Contact ---
const Footer = () => {
  const { CONTACT, UI } = useData();
  return (
  <footer data-testid="footer-section" className="paper-deep" style={{ borderTop: "1px solid rgba(201,162,75,0.4)" }}>
    <div className="max-w-6xl mx-auto px-6 py-14 flex flex-col md:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-4">
        <Logo size={68} variant="light" />
        <div>
          <div className="font-serif-display text-2xl" style={{ color: "#f4ecd8" }}>WILLIAMS Golf Therapy</div>
          <div className="font-sans-clean text-xs tracking-[0.3em] uppercase" style={{ color: "#c9a24b" }}>{UI.footer.pillars}</div>
        </div>
      </div>
      <div className="text-center md:text-right font-sans-clean text-sm" style={{ color: "#e9ddbb" }}>
        <div>{CONTACT.address}</div>
        <div className="mt-1">{CONTACT.founder}</div>
        <div className="mt-1">
          <a href={`mailto:${CONTACT.email}`} className="hover:text-[#c9a24b] transition-colors">{CONTACT.email}</a>
          <span className="opacity-50 mx-2">·</span>
          <a href={`tel:${CONTACT.phoneTel}`} className="hover:text-[#c9a24b] transition-colors">{CONTACT.phone}</a>
        </div>
        <div className="mt-3 text-xs opacity-70">© {new Date().getFullYear()} WILLIAMS Golf Therapy. {UI.footer.confidential}</div>
      </div>
    </div>
  </footer>
  );
};

// --- 5-Year Projection Chart (Recharts) ---
const chartFmt = (n) => {
  if (Math.abs(n) >= 1_000_000) return "$" + (n / 1_000_000).toFixed(1) + "M";
  if (Math.abs(n) >= 1_000) return "$" + Math.round(n / 1_000) + "K";
  return "$" + n;
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="paper rounded-sm px-4 py-3 shadow-lg" style={{ border: "1px solid var(--wgt-green-700)" }}>
      <div className="font-serif-display text-lg" style={{ color: "var(--wgt-green-900)" }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-sm font-sans-clean mt-1" style={{ color: "var(--wgt-ink)" }}>
          <span className="inline-block w-3 h-3 rounded-full" style={{ background: p.color }} />
          <span className="opacity-70">{p.name}:</span>
          <span className="font-semibold">{chartFmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const ProjectionChart = () => {
  const { PROJECTIONS, UI } = useData();
  const chartData = PROJECTIONS.map(p => ({
    year: p.year,
    [UI.financials.chartLegendRevenue]: p.revenue,
    [UI.financials.chartLegendProfit]: p.netProfit,
  }));
  const revenueKey = UI.financials.chartLegendRevenue;
  const profitKey = UI.financials.chartLegendProfit;
  return (
  <div className="paper p-6 md:p-8 rounded-sm mb-12 print-avoid-break">
    <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
      <div>
        <div className="font-sans-clean text-[11px] tracking-[0.3em] uppercase mb-1" style={{ color: "var(--wgt-gold-dark)" }}>
          {UI.financials.chartEyebrow}
        </div>
        <div className="font-serif-display text-2xl md:text-3xl" style={{ color: "var(--wgt-green-900)" }}>
          {UI.financials.chartTitle}
        </div>
      </div>
      <div className="flex items-center gap-5 font-sans-clean text-xs" style={{ color: "var(--wgt-ink-soft)" }}>
        <div className="flex items-center gap-2"><span className="inline-block w-4 h-2 rounded-sm" style={{ background: "#155a3f" }} />{revenueKey}</div>
        <div className="flex items-center gap-2"><span className="inline-block w-4 h-2 rounded-sm" style={{ background: "#c9a24b" }} />{profitKey}</div>
      </div>
    </div>
    <div style={{ width: "100%", height: 340 }} data-testid="projection-chart">
      <ResponsiveContainer>
        <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
          <defs>
            <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#155a3f" stopOpacity={0.85} />
              <stop offset="100%" stopColor="#155a3f" stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#c9a24b" stopOpacity={0.55} />
              <stop offset="100%" stopColor="#c9a24b" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 6" stroke="#cdbf9b" vertical={false} />
          <XAxis dataKey="year" stroke="#3a3a3a" tick={{ fontFamily: "Inter", fontSize: 12, fill: "#3a3a3a" }} tickLine={false} axisLine={{ stroke: "#cdbf9b" }} />
          <YAxis stroke="#3a3a3a" tick={{ fontFamily: "Inter", fontSize: 12, fill: "#3a3a3a" }} tickFormatter={chartFmt} tickLine={false} axisLine={{ stroke: "#cdbf9b" }} />
          <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#c9a24b", strokeWidth: 1, strokeDasharray: "4 4" }} />
          <ReferenceLine y={0} stroke="#0b3d2e" strokeWidth={1.2} />
          <Area type="monotone" dataKey={revenueKey} stroke="#0b3d2e" strokeWidth={2.5} fill="url(#revGrad)" />
          <Area type="monotone" dataKey={profitKey} stroke="#8f6f2b" strokeWidth={2} fill="url(#profitGrad)" />
          <Line type="monotone" dataKey={profitKey} stroke="#c9a24b" strokeWidth={3} dot={{ r: 5, fill: "#c9a24b", stroke: "#0b3d2e", strokeWidth: 2 }} activeDot={{ r: 7 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
    <div className="font-sans-clean text-xs mt-4" style={{ color: "var(--wgt-ink-soft)" }}>
      {UI.financials.chartFootnote}
    </div>
  </div>
  );
};

// --- Competitive Comparison Table ---
const cellStyle = (val, labels) => {
  const lbl = (labels && labels[val]) || val;
  switch (val) {
    case "full":     return { icon: <Check size={18} />, color: "#155a3f", bg: "rgba(21,90,63,0.12)", label: lbl };
    case "partial":
    case "limited":
    case "referral":
    case "partner":
    case "cert-only":
    case "per lesson":
    case "per visit":
      return { icon: <CircleDot size={18} />, color: "#8f6f2b", bg: "rgba(201,162,75,0.18)", label: lbl };
    case "none":
    case "n/a":
    default:
      return { icon: <Minus size={18} />, color: "#9a9a9a", bg: "rgba(0,0,0,0.04)", label: lbl };
  }
};

const ComparisonTable = () => {
  const { COMPARISON, UI } = useData();
  return (
  <section data-testid="comparison-section" className="max-w-6xl mx-auto px-6 py-20 print-break-before">
    <SectionTitle eyebrow={UI.comparison.eyebrow} title={UI.comparison.title} kicker={UI.comparison.kicker} />
    <div className="paper rounded-sm overflow-x-auto print-avoid-break">
      <table className="w-full text-sm font-sans-clean" data-testid="comparison-table">
        <thead>
          <tr style={{ background: "var(--wgt-green-800)", color: "var(--wgt-cream-100)" }}>
            <th className="text-left p-4 font-serif-display text-base font-semibold">{UI.comparison.capability}</th>
            {COMPARISON.competitors.map((c, i) => (
              <th key={i} className={`p-4 font-serif-display text-base font-semibold ${i === 0 ? "border-l-4" : ""}`}
                style={i === 0 ? { borderColor: "#c9a24b", color: "#c9a24b" } : {}}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {COMPARISON.rows.map((row, i) => (
            <tr key={i} className="border-b" style={{ borderColor: "var(--wgt-cream-200)" }} data-testid={`comparison-row-${i}`}>
              <td className="p-4 font-medium" style={{ color: "var(--wgt-green-900)" }}>{row.feature}</td>
              {row.values.map((v, j) => {
                const s = cellStyle(v, UI.comparison.cellLabels);
                return (
                  <td key={j} className={`p-4 text-center ${j === 0 ? "border-l-4" : ""}`} style={j === 0 ? { borderColor: "#c9a24b", background: "rgba(201,162,75,0.05)" } : {}}>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full" style={{ background: s.bg, color: s.color }}>
                      {s.icon}
                      <span className="text-xs">{s.label}</span>
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="mt-4 text-xs font-sans-clean" style={{ color: "var(--wgt-ink-soft)" }}>
      {UI.comparison.footnote}
    </div>
  </section>
  );
};

// --- Contact ---
const useCopyFeedback = (toastTextFn) => {
  const [toast, setToast] = useState(null);
  const fire = async (text, label) => {
    const message = toastTextFn ? toastTextFn(label) : `✓ ${label} copied to clipboard`;
    try {
      await navigator.clipboard.writeText(text);
      setToast({ label: message, key: Date.now() });
      setTimeout(() => setToast(null), 2400);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); setToast({ label: message, key: Date.now() }); setTimeout(() => setToast(null), 2400); } catch (_e) { /* clipboard unavailable */ }
      document.body.removeChild(ta);
    }
  };
  return { toast, fire };
};

const Contact = () => {
  const { CONTACT, LOCATION, INVESTOR_CTA, UI } = useData();
  const { toast, fire } = useCopyFeedback((label) => `✓ ${label}`);

  const gmailComposeUrl = (() => {
    const base = "https://mail.google.com/mail/?view=cm&fs=1";
    // Decode the mailto: href to extract subject + body localized to current lang
    const m = INVESTOR_CTA.href.match(/subject=([^&]+)&body=([^&]+)/);
    const subject = m ? m[1] : encodeURIComponent("Investor Inquiry — WILLIAMS Golf Therapy");
    const body = m ? m[2] : "";
    return `${base}&to=${encodeURIComponent(CONTACT.email)}&su=${subject}&body=${body}`;
  })();

  const handleEmail = () => {
    window.open(gmailComposeUrl, "_blank", "noopener,noreferrer");
    fire(CONTACT.email, UI.contact.toastEmail);
  };
  const handlePhone = () => {
    window.location.href = `tel:${CONTACT.phoneTel}`;
    fire(CONTACT.phone, UI.contact.toastPhone);
  };

  return (
    <section id="contact" data-testid="contact-section" className="max-w-6xl mx-auto px-6 py-20 print-break-before">
      <SectionTitle eyebrow={UI.contact.eyebrow} title={UI.contact.title} kicker={UI.contact.kicker} />
      <div className="grid md:grid-cols-3 gap-6">
        <button
          type="button"
          data-testid="contact-email"
          onClick={handleEmail}
          className="paper p-7 rounded-sm flex flex-col items-start gap-3 transition-all hover:shadow-lg hover:-translate-y-0.5 print-avoid-break text-left"
          style={{ borderTop: "3px solid var(--wgt-gold)" }}
        >
          <div className="p-3 rounded-full" style={{ background: "var(--wgt-green-800)" }}>
            <Mail size={22} style={{ color: "var(--wgt-gold)" }} />
          </div>
          <div className="font-sans-clean text-[11px] tracking-[0.3em] uppercase" style={{ color: "var(--wgt-gold-dark)" }}>{UI.contact.emailLabel}</div>
          <div className="font-serif-display text-xl break-all" style={{ color: "var(--wgt-green-900)" }}>{CONTACT.email}</div>
          <div className="font-sans-clean text-xs mt-1 flex items-center gap-1.5" style={{ color: "var(--wgt-ink-soft)" }}>
            <Copy size={12} /> {UI.contact.emailHint}
          </div>
        </button>

        <button
          type="button"
          data-testid="contact-phone"
          onClick={handlePhone}
          className="paper p-7 rounded-sm flex flex-col items-start gap-3 transition-all hover:shadow-lg hover:-translate-y-0.5 print-avoid-break text-left"
          style={{ borderTop: "3px solid var(--wgt-gold)" }}
        >
          <div className="p-3 rounded-full" style={{ background: "var(--wgt-green-800)" }}>
            <Phone size={22} style={{ color: "var(--wgt-gold)" }} />
          </div>
          <div className="font-sans-clean text-[11px] tracking-[0.3em] uppercase" style={{ color: "var(--wgt-gold-dark)" }}>{UI.contact.phoneLabel}</div>
          <div className="font-serif-display text-2xl" style={{ color: "var(--wgt-green-900)" }}>{CONTACT.phone}</div>
          <div className="font-sans-clean text-xs mt-1 flex items-center gap-1.5" style={{ color: "var(--wgt-ink-soft)" }}>
            <Copy size={12} /> {UI.contact.phoneHint}
          </div>
        </button>

        <div
          data-testid="contact-address"
          className="paper p-7 rounded-sm flex flex-col items-start gap-3 print-avoid-break"
          style={{ borderTop: "3px solid var(--wgt-gold)" }}
        >
          <div className="p-3 rounded-full" style={{ background: "var(--wgt-green-800)" }}>
            <MapPin size={22} style={{ color: "var(--wgt-gold)" }} />
          </div>
          <div className="font-sans-clean text-[11px] tracking-[0.3em] uppercase" style={{ color: "var(--wgt-gold-dark)" }}>{UI.contact.visitLabel}</div>
          <div className="font-serif-display text-xl leading-snug" style={{ color: "var(--wgt-green-900)" }}>{CONTACT.address}</div>
          <div className="font-sans-clean text-xs mt-1" style={{ color: "var(--wgt-ink-soft)" }}>{LOCATION.proximity}</div>
        </div>
      </div>

      <div className="mt-10 paper-deep rounded-sm p-8 md:p-10 text-center relative grain print-avoid-break">
        <div className="font-sans-clean text-[11px] tracking-[0.3em] uppercase mb-3" style={{ color: "#c9a24b" }}>
          {CONTACT.founder}
        </div>
        <div className="font-serif-display text-3xl md:text-4xl max-w-2xl mx-auto" style={{ color: "#f4ecd8" }}>
          &ldquo;{UI.contact.quote}&rdquo;
        </div>
      </div>

      {toast && (
        <div
          key={toast.key}
          data-testid="contact-toast"
          className="no-print fixed bottom-8 left-1/2 -translate-x-1/2 z-[60] px-6 py-3 rounded-full font-sans-clean text-sm font-medium shadow-2xl"
          style={{
            background: "var(--wgt-green-800)",
            color: "var(--wgt-gold)",
            border: "1px solid var(--wgt-gold)",
            animation: "wgtFadeUp 0.3s ease-out",
          }}
        >
          {toast.label}
        </div>
      )}
    </section>
  );
};

// --- Page composition ---
const BusinessPlan = () => {
  const [lang, setLang] = useState("en");
  const DATA = lang === "es" ? DATA_ES : DATA_EN;
  return (
    <DataContext.Provider value={{ ...DATA, lang, setLang }}>
      <div data-testid="business-plan-page" className="min-h-screen">
        <Nav />
        <Hero />
        <Founder />
        <Market />
        <Differentiators />
        <ComparisonTable />
        <FacilityGallery />
        <Services />
        <Financials />
        <Investment />
        <Roadmap />
        <WhyNow />
        <Contact />
        <Footer />
      </div>
    </DataContext.Provider>
  );
};

export default BusinessPlan;
